import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";

import { OpenInNew, Send } from "@mui/icons-material";
import {
    // Avatar,
    Alert,
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from "@mui/material";

import { useAtomValue } from "jotai";
import { globalStore } from "../store/global.atoms";

import styles from "./ChatPage.module.css";

const PLAYGROUND_INSTRUCTIONS_DISMISSED_KEY = "playgroundInstructionsDismissed";

const ChatPage = () => {
    const backendEndpoint = process.env.REACT_APP_BACKEND_ENDPOINT;
    const wsEndpoint = backendEndpoint
        .replace(/^https:\/\//, "wss://")
        .replace(/^http:\/\//, "ws://");
    const socket = useRef(null);

    const tenantIdAtom = useAtomValue(globalStore.userDataAtom)?.tenantId;
    const { tenantId: tenantIdQueryParam } = useParams();
    const tenantId = tenantIdAtom ? tenantIdAtom : tenantIdQueryParam;

    const [sessionId, setSessionId] = useState("");

    const [companyName, setCompanyName] = useState("");
    const [agentName, setAgentName] = useState("");
    const [messages, setMessages] = useState([]);
    const [showPlaygroundInstructions, setShowPlaygroundInstructions] =
        useState(
            () =>
                sessionStorage.getItem(
                    PLAYGROUND_INSTRUCTIONS_DISMISSED_KEY,
                ) !== "true",
        );

    const handleDismissPlaygroundInstructions = () => {
        sessionStorage.setItem(PLAYGROUND_INSTRUCTIONS_DISMISSED_KEY, "true");
        setShowPlaygroundInstructions(false);
    };

    const scrollRef = useRef(null);

    const [newMessage, setNewMessage] = useState("");

    const escapeHtml = (text) => {
        return text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    };

    const linkify = (inputText) => {
        // From:https://stackoverflow.com/questions/49634850/convert-plain-text-links-to-clickable-links

        var replacedText, replacePattern1, replacePattern2, replacePattern3;

        //URLs starting with http://, https://, or ftp://
        replacePattern1 =
            /(\b(https?|ftp):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gim;
        replacedText = inputText.replace(
            replacePattern1,
            '<a href="$1" target="_blank">$1</a>',
        );

        //URLs starting with "www." (without // before it, or it'd re-link the ones done above).
        replacePattern2 = /(^|[^/])(www\.[\S]+(\b|$))/gim;
        replacedText = replacedText.replace(
            replacePattern2,
            '$1<a href="http://$2" target="_blank">$2</a>',
        );

        //Change email addresses to mailto:: links.
        replacePattern3 =
            /(([a-zA-Z0-9\-_.])+@[a-zA-Z_]+?(\.[a-zA-Z]{2,6})+)/gim;
        replacedText = replacedText.replace(
            replacePattern3,
            '<a href="mailto:$1">$1</a>',
        );

        return replacedText;
    };

    const formatDate = (date) => {
        // const day = date.getDate();
        // const month = date.getMonth() + 1;
        // const year = date.getFullYear();
        // let datePart = [day, month, year]
        //     .map((n, i) => n.toString().padStart(i === 2 ? 4 : 2, "0"))
        //     .join("/");

        const hour = date.getHours();
        const minute = date.getMinutes();
        let timePart = [hour, minute]
            .map((n, i) => n.toString().padStart(2, "0"))
            .join(":");

        // return `${datePart} ${timePart}`;
        return timePart;
    };

    const handlePressEnter = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSendUserMessage();
        }
    };

    const createSession = useCallback(async () => {
        const response = await fetch(
            `${backendEndpoint}/create_session/${tenantId}/`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    source: "public_chat",
                    customerId: null,
                    conversationAttrs: {
                        id: "N/A",
                        labels: [],
                        attributes: {},
                        channel: {
                            name: "Public Chat",
                            type: "Playground",
                            source: "Web Browser",
                            description:
                                "A web interface to test the conversation",
                        },
                    },
                    contactAttrs: {
                        id: "N/A",
                        name: "Unknown",
                        email: "Unknown",
                        phoneNumber: "Unknown",
                        labels: [],
                        notes: [],
                        attributes: {},
                        description:
                            "An unauthanticated user using a web interface",
                    },
                }),
            },
        );

        if (!response.ok) {
            return "";
        }

        const json = await response.json();
        const { sessionId } = json;

        setSessionId(sessionId);
        return sessionId;
    }, [backendEndpoint, setSessionId, tenantId]);

    const handleSendUserMessage = useCallback(() => {
        const userMessage = newMessage;
        if (userMessage.trim() === "") return;
        setNewMessage("");
        setMessages([
            ...messages,
            {
                text: userMessage,
                sender: "user",
                dateTime: formatDate(new Date()),
            },
        ]);
    }, [messages, newMessage]);

    const handleSendBotMessage = useCallback(
        async (userMessage) => {
            if (sessionId === "") {
                createSession();
                return;
            }

            while (
                !socket.current ||
                socket.current.readyState !== WebSocket.OPEN
            ) {
                console.log("Waiting for WebSocket connection...");
                await new Promise((resolve) => setTimeout(resolve, 100));
            }

            socket.current.send(
                JSON.stringify({
                    question: userMessage,
                }),
            );
        },
        [sessionId, createSession, socket],
    );

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: "smooth" });
        }

        const lastMessage = messages.at(-1);
        if (lastMessage && lastMessage.sender === "user") {
            handleSendBotMessage(lastMessage.text);
        }
    }, [messages, handleSendBotMessage]);

    useEffect(() => {
        if (!tenantId) {
            return;
        }

        const run = async () => {
            const response = await fetch(
                `${backendEndpoint}/answer/${tenantId}/`,
                {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                },
            );

            const json = await response.json();
            const companyName = json.companyName;
            const agentName = json.agentName;

            if (companyName !== null) {
                setCompanyName(companyName);
                setAgentName(agentName);
            }
        };
        run();
    }, [tenantId, backendEndpoint]);

    const connectWebsocket = useCallback(() => {
        socket.current = new WebSocket(
            `${wsEndpoint}/answer/${tenantId}/${sessionId}/ws`,
        );

        socket.current.onmessage = (event) => {
            const data = JSON.parse(event.data);
            console.log(data);
            const message = data.answer;

            setMessages((prevMessages) => [
                ...prevMessages,
                {
                    text: message,
                    sender: "bot",
                    dateTime: formatDate(new Date()),
                },
            ]);
        };

        socket.current.onclose = async () => {
            console.log("WebSocket connection closed");
            if (
                !socket.current ||
                socket.current.readyState !== WebSocket.OPEN
            ) {
                console.log("Retrying WebSocket connection...");
                connectWebsocket();

                if (
                    socket.current &&
                    socket.current.readyState === WebSocket.OPEN
                ) {
                    console.log("WebSocket connection restablished!");
                }
            }
        };

        socket.current.onerror = (error) => {
            console.error("WebSocket error");
        };
    }, [tenantId, sessionId, wsEndpoint]);

    useEffect(() => {
        if (!tenantId || !sessionId) {
            return;
        }

        socket.current = new WebSocket(
            `${wsEndpoint}/answer/${tenantId}/${sessionId}/ws`,
        );

        connectWebsocket();

        return () => {
            socket.current.close();
        };
    }, [wsEndpoint, tenantId, sessionId, connectWebsocket]);

    return (
        <>
            <Container
                maxWidth={false}
                sx={{
                    minHeight: "100%",
                    display: "flex",
                    alignItems: { xs: "flex-start", lg: "center" },
                }}
                id="chat-container"
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "minmax(0, 1fr)",
                            lg: "minmax(0, 600px) minmax(320px, 1fr)",
                        },
                        alignItems: "center",
                        alignContent: "start",
                        gap: 4,
                        width: "100%",
                        maxWidth: 1200,
                        mx: "auto",
                        p: { xs: 1, sm: 2 },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100vh",
                            maxHeight: "760px",
                            minHeight: "560px",
                            paddingBottom: 2,
                            boxSizing: "border-box",
                            border: "1px solid #e5e9f0",
                            boxShadow: "0 2px 12px rgba(16, 24, 40, 0.06)",
                            borderRadius: "40px 40px 12px 12px",
                        }}
                    >
                        <Typography
                            variant="h4"
                            gutterBottom
                            className={styles.title}
                        >
                            <span>📢</span> {companyName}
                            {tenantIdAtom && (
                                <Link
                                    style={{
                                        marginLeft: "16px",
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                    to={`/chat/${tenantId}`}
                                    target="_blank"
                                >
                                    <OpenInNew />
                                </Link>
                            )}
                        </Typography>

                        {messages.length === 0 ? (
                            <Typography
                                variant="h6"
                                sx={{
                                    flexGrow: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginBottom: 2,
                                }}
                            >
                                Envie-nos sua mensagem.
                            </Typography>
                        ) : null}

                        <List
                            sx={{
                                width: "100%",
                                flexGrow: 1,
                                overflowY: "auto",
                                marginBottom: 2,
                            }}
                        >
                            {messages.map((message, index) => (
                                <ListItem
                                    ref={scrollRef}
                                    key={index}
                                    className={
                                        message.sender === "bot"
                                            ? styles.botMessageWrapper
                                            : styles.userMessageWrapper
                                    }
                                    sx={{
                                        display: "flex",
                                        flexDirection:
                                            message.sender === "bot"
                                                ? "row"
                                                : "row-reverse",
                                        textAlign:
                                            message.sender === "bot"
                                                ? "left"
                                                : "right",
                                        whiteSpace: "pre-line",
                                    }}
                                >
                                    {/*
                            <Avatar
                                className={
                                    message.sender === "bot"
                                        ? styles.botMessageAvatar
                                        : styles.userMessageAvatar
                                }
                            >
                                {message.sender === "bot" ? "🤖" : "👤"} 
                            </Avatar>
                            */}
                                    <ListItemText
                                        sx={{
                                            marginRight: 2,
                                            marginLeft: 2,
                                            display: "flex",
                                            alignItems: "self-end",
                                        }}
                                        className={styles.bubbleMessageBox}
                                        primary={
                                            <div
                                                className={
                                                    message.sender === "bot"
                                                        ? styles.botMessagePrimaryText
                                                        : styles.userMessagePrimaryText
                                                }
                                                dangerouslySetInnerHTML={{
                                                    __html: linkify(
                                                        escapeHtml(
                                                            message.text,
                                                        ),
                                                    ),
                                                }}
                                            />
                                        }
                                        secondary={
                                            <div
                                                className={
                                                    message.sender === "bot"
                                                        ? styles.botMessageSecondaryText
                                                        : styles.userMessageSecondaryText
                                                }
                                            >
                                                {message.sender === "bot"
                                                    ? `${agentName} - ${message.dateTime}`
                                                    : `Você - ${message.dateTime}`}
                                            </div>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>

                        <Box
                            id="chat-input"
                            className={styles.chatInputWrapper}
                        >
                            <TextField
                                id="chat-textarea"
                                className={styles.chatInput}
                                variant="outlined"
                                placeholder="Digite sua mensagem..."
                                fullWidth
                                inputRef={(input) => input && input.focus()}
                                autoComplete="off"
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                multiline
                                maxRows={6}
                                onKeyDown={handlePressEnter}
                            />
                            <Button
                                id="send-message-btn"
                                className={styles.sendMessageButton}
                                variant="contained"
                                endIcon={<Send />}
                                onClick={handleSendUserMessage}
                            />
                        </Box>
                    </Box>

                    <Paper
                        component="aside"
                        variant="outlined"
                        sx={{
                            p: 3,
                            borderColor: "#e5e9f0",
                            boxShadow: "0 2px 12px rgba(16, 24, 40, 0.06)",
                            borderRadius: "12px",
                        }}
                    >
                        <Typography variant="h5" gutterBottom>
                            Como funciona o Playground
                        </Typography>
                        <PlaygroundInstructions />
                    </Paper>
                </Box>
            </Container>

            {/* Playground Instructions Dialog */}
            <Dialog
                open={showPlaygroundInstructions}
                disableEscapeKeyDown
                fullWidth
                aria-labelledby="playground-instructions-title"
            >
                <DialogTitle id="playground-instructions-title">
                    Como funciona o Playground
                </DialogTitle>
                <DialogContent dividers>
                    <PlaygroundInstructions />
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={handleDismissPlaygroundInstructions}
                    >
                        Entendi as regras do playground
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const PlaygroundInstructions = () => (
    <>
        <Typography paragraph>
            O Playground é um ambiente de testes para você conversar com a IA,
            avaliar as respostas e identificar melhorias antes de colocar o
            agente em produção.
        </Typography>
        <Typography fontWeight="bold">O que você pode fazer aqui?</Typography>
        <List dense>
            <ListItem alignItems="flex-start">
                <ListItemText
                    primary={
                        <>
                            <strong>Testar conversas:</strong> simule situações
                            reais dos seus clientes.
                        </>
                    }
                />
            </ListItem>
            <ListItem alignItems="flex-start">
                <ListItemText
                    primary={
                        <>
                            <strong>Avaliar as respostas:</strong> confira se a
                            IA responde corretamente e segue as orientações do
                            treinamento.
                        </>
                    }
                />
            </ListItem>
            <ListItem alignItems="flex-start">
                <ListItemText
                    primary={
                        <>
                            <strong>Testar integrações:</strong> verifique se a
                            IA consulta os sistemas conectados e executa os
                            fluxos esperados.
                        </>
                    }
                />
            </ListItem>
            <ListItem alignItems="flex-start">
                <ListItemText
                    primary={
                        <>
                            <strong>Melhorar o treinamento:</strong> identificou
                            algo que pode melhorar? Acesse o menu Treinamento da
                            IA, faça os ajustes e teste novamente.
                        </>
                    }
                />
            </ListItem>
        </List>
        <Alert severity="info">
            <strong>Importante:</strong> o Playground serve apenas para
            simulações. Ações como transferir uma conversa para um atendente,
            finalizar o atendimento, criar tags ou gerenciar usuários não são
            executadas aqui. Para utilizar essas funcionalidades no atendimento
            real, acesse o Chiko Inbox.
        </Alert>
    </>
);

export default ChatPage;
